import React from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import currency from 'helpers/currency';
import { readMoneyChain } from './orderTokens';

/**
 * A cadeia do dinheiro de um pedido, linha a linha.
 *
 * É o motivo de a aba existir: o produtor via só "R$ 91,53" e "R$ 81,00" e
 * tinha de acreditar na diferença. Aqui cada elo é uma linha nomeada, e os dois
 * totais — o que o comprador pagou e o que sobra — ficam separados por régua,
 * porque são as duas únicas linhas que ele costuma conferir contra o extrato.
 *
 * Linhas de valor zero não são impressas: um extrato cheio de "R$ 0,00" faz o
 * olho procurar o que importa no meio de ruído.
 */
export default function OrderMoneyChain({ transaction }) {
    const chain = readMoneyChain(transaction);
    const installments = Number(transaction?.installments) || 1;

    return (
        <Ledger>
            <Row>
                <Label>Itens do pedido</Label>
                <Value>{currency.format(chain.subtotal)}</Value>
            </Row>

            {chain.discount > 0 && (
                <Row>
                    <Label>Desconto concedido</Label>
                    <Value $tone="negative">− {currency.format(chain.discount)}</Value>
                </Row>
            )}

            {chain.serviceFee > 0 && (
                <Row>
                    <Label>Taxa de serviço cobrada do comprador</Label>
                    <Value>+ {currency.format(chain.serviceFee)}</Value>
                </Row>
            )}

            {chain.interest > 0 && (
                <Row>
                    <Label>Juros do parcelamento ({installments}x)</Label>
                    <Value>+ {currency.format(chain.interest)}</Value>
                </Row>
            )}

            <Row $emphasis>
                <Label $strong>{chain.moved ? 'O comprador pagou' : 'O comprador pagaria'}</Label>
                <Value $strong $tone={chain.moved ? 'default' : 'idle'}>
                    {currency.format(chain.paid)}
                </Value>
            </Row>

            {chain.kept > 0 && (
                <Row>
                    <Label>{chain.moved ? 'Retido pela plataforma' : 'Seria retido pela plataforma'}</Label>
                    <Value $tone={chain.moved ? 'negative' : 'idle'}>− {currency.format(chain.kept)}</Value>
                </Row>
            )}

            <Row $emphasis>
                <Label $strong>{chain.moved ? 'Sobra para você' : 'Sobraria para você'}</Label>
                <Value $strong $tone={chain.creditsBalance ? 'positive' : 'idle'}>
                    {currency.format(chain.net)}
                </Value>
            </Row>

            {chain.absorbedFee && (
                <Note>
                    A taxa de serviço deste pedido foi absorvida por você: ela não entrou no
                    valor cobrado do comprador, saiu do seu líquido.
                </Note>
            )}

            {!chain.creditsBalance && (
                <Note>
                    {chain.moved
                        ? 'Este pedido saiu do seu saldo: o valor foi devolvido ao comprador ou está em disputa.'
                        : 'Nada foi cobrado neste pedido. Os valores acima são o que ele teria rendido se o pagamento tivesse sido concluído.'}
                </Note>
            )}
        </Ledger>
    );
}

const Ledger = styled.div`
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding: 7px 0;

    ${({ $emphasis }) => $emphasis && `
        border-top: 1px solid ${colors.neutralDark};
        margin-top: 4px;
        padding-top: 10px;
    `}
`;

const Label = styled.span`
    font-size: 13px;
    color: ${({ $strong }) => $strong ? colors.nightDark : colors.night};
    font-weight: ${({ $strong }) => $strong ? '600' : '400'};
    line-height: 1.35;
`;

const Value = styled.span`
    font-size: ${({ $strong }) => $strong ? '14px' : '13px'};
    font-weight: ${({ $strong }) => $strong ? '600' : '400'};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: ${({ $tone }) => {
        if ($tone === 'negative') return '#B3261E';
        if ($tone === 'positive') return '#157F3C';
        if ($tone === 'idle') return colors.nightLight;
        return colors.nightDark;
    }};
`;

const Note = styled.p`
    margin-top: 10px;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;
